import axios, { AxiosInstance, AxiosResponse } from 'axios';
// import { API_CONFIG } from './constants';

// API Response interface
export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
  errors?: Array<{
    type: string;
    value: any;
    msg: string;
    path: string;
    location: string;
  }>;
  details?: Array<{
    type: string;
    value: any;
    msg: string;
    path: string;
    location: string;
  }>;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// User interfaces
export interface User {
  id: string;
  _id?: string; // Optional backend ID
  name: string;
  email: string;
  phone?: string;
  role: 'super_admin' | 'admin' | 'temple_admin' | 'priest' | 'devotee';
  permissions?: string[];
  status?: 'active' | 'inactive' | 'suspended';
  isActive?: boolean; // Backend status field
  templeId?: string;
  temple?: string; // For temple admin role, this will contain the temple name/ID
  isEmailVerified: boolean;
  profileImage?: string;
  address?: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  preferences?: {
    notifications: boolean;
    newsletter: boolean;
    language: string;
  };
  createdAt: string;
  updatedAt: string;
}

// Temple interfaces
export interface Temple {
  _id: string;
  id?: string; // For backward compatibility
  name: string;
  slug: string;
  description: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    coordinates?: {
      lat: number;
      lng: number;
    };
  };
  contact: {
    phone: string;
    email: string;
    website?: string;
  };
  images: string[];
  banner?: string; // Banner image for featured display
  deity: string;
  establishedYear?: number;
  timings: {
    monday: string;
    tuesday: string;
    wednesday: string;
    thursday: string;
    friday: string;
    saturday: string;
    sunday: string;
  };
  facilities: string[];
  admin: User;
  priests: User[];
  status?: 'active' | 'inactive' | 'maintenance';
  isActive: boolean;
  subscriptionPlan?: string;
  subscriptionStatus?: 'active' | 'expired' | 'cancelled' | 'trial';
  subscriptionStartDate?: string;
  subscriptionEndDate?: string;
  stripeAccountId?: string;
  createdAt: string;
  updatedAt: string;
}

// Donation interfaces
export interface Donation {
  id: string;
  donor: User;
  donorName?: string;
  donorEmail?: string;
  temple: Temple;
  amount: number;
  currency: string;
  type: 'general' | 'specific' | 'monthly' | 'festival' | 'one-time' | 'temple';
  purpose?: string;
  isAnonymous: boolean;
  paymentIntentId: string;
  stripeSessionId?: string;
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  receiptNumber: string;
  taxDeductible: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// Pooja interfaces
export interface Pooja {
  _id: string;
  id: string;
  name: string;
  deity?: string;
  description: string;
  duration: number;
  price: number;
  atTemplePrice?: number;
  atHomePrice?: number;
  currency: string;
  temple: Temple;
  type?: 'online' | 'offline' | 'both';
  locationType?: 'temple' | 'home' | 'both';
  isActive: boolean;
  isPopular?: boolean;
  category?: string;
  requirements?: string[];
  benefits?: string[];
  images?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface PoojaBooking {
  id: string;
  devotee: User;
  temple: Temple;
  pooja: Pooja;
  scheduledDate: string;
  scheduledTime: string;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled';
  amount: number;
  currency: string;
  specialRequests?: string;
  contactInfo: {
    name: string;
    phone: string;
    email: string;
  };
  paymentStatus?: 'pending' | 'paid' | 'failed' | 'refunded';
  paymentMethod?: 'online' | 'cash';
  createdAt: string;
  updatedAt: string;
}

export interface TicketMessage {
  _id: string;
  sender: User | string;
  senderRole: 'super_admin' | 'temple_admin';
  message: string;
  attachments?: {
    name: string;
    url: string;
    type: string;
    size: number;
  }[];
  createdAt: string;
}

export interface Ticket {
  _id: string;
  ticketId: string;
  subject: string;
  description: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  temple: Temple | string;
  createdBy: User | string;
  attachments?: {
    name: string;
    url: string;
    type: string;
    size: number;
  }[];
  messages: TicketMessage[];
  lastReplyAt?: string;
  resolvedAt?: string;
  closedAt?: string;
  createdAt: string;
  updatedAt: string;
}

// Page interface
export interface Page {
  _id: string;
  title: string;
  slug: string;
  content: string;
  metaTitle?: string;
  metaDescription?: string;
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
}

// Analytics interfaces
export interface DashboardStats {
  overview?: {
    totalTemples: number;
    totalTempleAdmins: number;
    totalPriests: number;
    totalDevotees: number;
    totalPoojas: number;
    totalPoojaBookings: number;
    totalEvents: number;
    totalEventBookings: number;
    totalSponsorships: number;
    totalSponsorshipBookings: number;
    totalDonations: number;
    totalRevenue: number;
    totalOnlineRevenue: number;
    pendingApprovals: number;
    pendingVerifications: number;
  };
  booking?: {
    poojaBookings: { total: number; confirmed: number; pending: number; completed: number; cancelled: number };
    eventBookings: { total: number; confirmed: number; pending: number; completed: number; cancelled: number };
  };
  revenue?: {
    total: number;
    online: number;
    offline: number;
    byPooja: number;
    byEvents: number;
    bySponsorships: number;
    donations: number;
  };
  recentActivities?: Array<{
    id: string;
    type: string;
    message: string;
    time: string;
    date: string;
    user?: string;
    amount?: number;
  }>;
  pendingActions?: Array<{
    id: string;
    type: string;
    title: string;
    date: string;
    link: string;
  }>;
  // Legacy fields for backward compatibility
  totalDonations?: number;
  totalDonors?: number;
  totalTemples?: number;
  totalPoojaBookings?: number;
  monthlyDonations?: Array<{ month: string; amount: number; count: number }>;
  recentDonations?: Donation[];
  recentBookings?: PoojaBooking[];
}

// API Client Class
class AdminApiClient {
  private client: AxiosInstance;
  private baseURL: string;

  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';
    
    // 初始化 Axios 实例
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token and handle form data
    this.client.interceptors.request.use(
      (config) => {
        const token = this.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        // For FormData requests, delete Content-Type to allow browser to set boundary
        if (config.data instanceof FormData) {
          delete config.headers['Content-Type'];
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response: AxiosResponse<ApiResponse>) => {
        return response;
      },
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid
          this.removeToken();
          if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Token management
  private getToken(): string | null {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('admin_auth_token');
    }
    return null;
  }

  private setToken(token: string): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem('admin_auth_token', token);
    }
  }

  private removeToken(): void {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('admin_auth_token');
    }
  }

  logout(): void {
    this.removeToken();
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  }

  // Authentication APIs
  async login(credentials: {
    email: string;
    password: string;
  }): Promise<ApiResponse<{ user: User; token: string }>> {
    const response = await this.client.post('/auth/login', credentials);
    // Server returns { success: true, token: "...", user: {...} }
    if (response.data.success && response.data.token) {
      this.setToken(response.data.token);
      // Return in expected format for AuthContext
      return {
        success: true,
        data: {
          token: response.data.token,
          user: response.data.user
        },
        message: response.data.message || 'Login successful'
      };
    }
    return response.data;
  }

  // Dashboard APIs
  async getDashboardStats(): Promise<ApiResponse<{ stats: DashboardStats }>> {
    const response = await this.client.get('/dashboard/stats');
    return response.data;
  }

  // Temple Management APIs
  async getTemples(params?: {
    page?: number;
    limit?: number;
    search?: string;
    state?: string;
    city?: string;
    deity?: string;
  }): Promise<ApiResponse<{ temples: Temple[]; pagination?: any }>> {
    const response = await this.client.get('/temples', { params });
    return response.data;
  }

  // --- Legacy (Old) Data APIs ---
  async importLegacyData(params: { datasetType: 'donors' | 'sales'; templeId?: string; file: File }): Promise<ApiResponse<any>> {
    const form = new FormData();
    form.append('datasetType', params.datasetType);
    if (params.templeId) form.append('templeId', params.templeId);
    form.append('file', params.file);
    const response = await this.client.post('/legacy-data/import', form);
    return response.data;
  }

  async getLegacyJobs(): Promise<ApiResponse<{ jobs: any[] }>> {
    const response = await this.client.get('/legacy-data/jobs');
    return response.data;
  }

  async getLegacyJobRecords(jobId: string, params?: {
    page?: number;
    limit?: number;
    q?: string;
    status?: string;
    donor_id?: number | string;
    startDate?: string;
    endDate?: string;
    includeMapped?: boolean;
  }): Promise<ApiResponse<any>> {
    const response = await this.client.get(`/legacy-data/jobs/${jobId}/records`, { params });
    return response.data;
  }

  async deleteLegacyJob(jobId: string): Promise<ApiResponse<any>> {
    const response = await this.client.delete(`/legacy-data/jobs/${jobId}`);
    return response.data;
  }

  async getLegacyDevotees(params?: { page?: number; limit?: number; q?: string; templeId?: string }): Promise<ApiResponse<any>> {
    const response = await this.client.get('/legacy-data/devotees', { params });
    return response.data;
  }

  async deleteLegacyDevotee(id: string): Promise<ApiResponse<any>> {
    const response = await this.client.delete(`/legacy-data/devotees/${id}`);
    return response.data;
  }

  async getLegacyTransactions(donorId: number, params?: { page?: number; limit?: number; startDate?: string; endDate?: string; transactionType?: string }): Promise<ApiResponse<any>> {
    const response = await this.client.get(`/legacy-data/transactions/${donorId}`, { params });
    return response.data;
  }

  async mergeLegacySalesJob(jobId: string, params?: { templeId?: string }): Promise<ApiResponse<any>> {
    const response = await this.client.post(`/legacy-data/jobs/${jobId}/merge`, {
      ...(params?.templeId ? { templeId: params.templeId } : {})
    });
    return response.data;
  }

  async getTempleById(id: string): Promise<ApiResponse<{ temple: Temple }>> {
    const response = await this.client.get(`/temples/${id}`);
    return response.data;
  }

  async createTemple(templeData: any): Promise<ApiResponse<{ temple: Temple }>> {
    if (templeData instanceof FormData) {
      // Use the FormData endpoint for requests with images
      const response = await this.client.post('/temples/with-images', templeData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } else {
      // Use the JSON endpoint for requests without images
      const response = await this.client.post('/temples', templeData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    }
  }

  async updateTemple(id: string, updateData: any): Promise<ApiResponse<{ temple: Temple }>> {
    if (updateData instanceof FormData) {
      // Use the FormData endpoint for requests with images
      const response = await this.client.put(`/temples/${id}/with-images`, updateData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } else {
      // Use the JSON endpoint for requests without images
      const response = await this.client.put(`/temples/${id}`, updateData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    }
  }

  // Admin: Update temple with images
  async updateTempleWithImages(templeId: string, formData: FormData): Promise<ApiResponse<any>> {
    console.log('🚀 Calling updateTempleWithImages API...');
    /* 
       Note: The backend expects 'logoFile', 'bannerFile' and 'images' in FormData.
       Ensure Component appends them correctly.
    */
    const response = await this.client.put(`/temples/${templeId}/with-images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  // Admin: Toggle temple visibility
  async toggleTempleVisibility(templeId: string, isActive: boolean): Promise<ApiResponse<any>> {
    const response = await this.client.put(`/temples/${templeId}/visibility`, { isActive });
    return response.data;
  }



  async deleteTemple(id: string): Promise<ApiResponse> {
    const response = await this.client.delete(`/temples/${id}`);
    return response.data;
  }

  async uploadTempleImages(id: string, files: File[]): Promise<ApiResponse<{ images: string[] }>> {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('images', file);
    });
    
    const response = await this.client.post(`/temples/${id}/images`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async deleteTempleImage(id: string, imageUrl: string): Promise<ApiResponse> {
    const response = await this.client.delete(`/temples/${id}/images`, {
      data: { imageUrl }
    });
    return response.data;
  }

  async addPriestToTemple(templeId: string, priestId: string): Promise<ApiResponse<{ temple: Temple }>> {
    const response = await this.client.post(`/temples/${templeId}/priests`, { priestId });
    return response.data;
  }

  async removePriestFromTemple(templeId: string, priestId: string): Promise<ApiResponse<{ temple: Temple }>> {
    const response = await this.client.delete(`/temples/${templeId}/priests`, {
      data: { priestId }
    });
    return response.data;
  }

  async getTempleStats(templeId: string): Promise<ApiResponse<{ stats: any }>> {
    const response = await this.client.get(`/temples/${templeId}/stats`);
    return response.data;
  }

  // Donation Management APIs
  async getDonations(params?: {
    page?: number;
    limit?: number;
    templeId?: string;
    donorId?: string;
    type?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<ApiResponse<{ donations: Donation[] }>> {
    const response = await this.client.get('/donations', { params });
    return response.data;
  }

  async getDonationById(id: string): Promise<ApiResponse<{ donation: Donation }>> {
    const response = await this.client.get(`/donations/${id}`);
    return response.data;
  }

  async updateDonationStatus(id: string, status: string, notes?: string): Promise<ApiResponse<{ donation: Donation }>> {
    const response = await this.client.put(`/donations/${id}/status`, { status, notes });
    return response.data;
  }

  async refundDonation(id: string, reason: string): Promise<ApiResponse<{ donation: Donation }>> {
    const response = await this.client.post(`/donations/${id}/refund`, { reason });
    return response.data;
  }

  async generateReceipt(id: string): Promise<Blob> {
    const response = await this.client.get(`/donations/${id}/receipt`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async getDonationStats(templeId?: string): Promise<ApiResponse<{ stats: any }>> {
    const response = await this.client.get('/donations/stats/overview', {
      params: templeId ? { templeId } : {}
    });
    return response.data;
  }

  async bulkImportDonations(templeId: string, donationsData: any[]): Promise<ApiResponse> {
    const response = await this.client.post(`/donations/${templeId}/bulk-import`, donationsData);
    return response.data;
  }

  async bulkDeleteDonations(donationIds: string[]): Promise<ApiResponse> {
    const response = await this.client.post(`/donations/bulk/delete`, { donationIds });
    return response.data;
  }

  // Pooja Management APIs
  async getPoojas(params?: {
    page?: number;
    limit?: number;
    templeId?: string;
    search?: string;
    minPrice?: number;
    maxPrice?: number;
  }): Promise<ApiResponse<{ poojas: Pooja[] }>> {
    const response = await this.client.get('/poojas/admin/list', { params });
    return response.data;
  }

  async getPoojaById(id: string): Promise<ApiResponse<{ pooja: Pooja }>> {
    const response = await this.client.get(`/poojas/${id}`);
    return response.data;
  }

  async createPooja(templeId: string, poojaData: any): Promise<ApiResponse<{ pooja: Pooja }>> {
    if (poojaData instanceof FormData) {
      const response = await this.client.post(`/poojas/${templeId}/with-images`, poojaData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } else {
      const response = await this.client.post(`/poojas/${templeId}`, poojaData);
      return response.data;
    }
  }

  async updatePooja(id: string, updateData: any): Promise<ApiResponse<{ pooja: Pooja }>> {
    if (updateData instanceof FormData) {
      const response = await this.client.put(`/poojas/${id}/with-images`, updateData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } else {
      const response = await this.client.put(`/poojas/${id}`, updateData);
      return response.data;
    }
  }

  async deletePooja(id: string): Promise<ApiResponse> {
    const response = await this.client.delete(`/poojas/${id}`);
    return response.data;
  }

  async deletePoojaImage(id: string, imageUrl: string): Promise<ApiResponse> {
    const response = await this.client.delete(`/poojas/${id}/images`, {
      data: { imageUrl }
    });
    return response.data;
  }

  async bulkImportPoojas(templeId: string, poojasData: any[]): Promise<ApiResponse> {
    const response = await this.client.post(`/poojas/${templeId}/bulk-import`, poojasData);
    return response.data;
  }

  async bulkDeletePoojas(poojaIds: string[]): Promise<ApiResponse> {
    const response = await this.client.post(`/poojas/bulk/delete`, { poojaIds });
    return response.data;
  }

  // Donation Campaign methods
  async getDonationCampaigns(): Promise<ApiResponse<{ campaigns: any[] }>> {
    const response = await this.client.get('/donation-campaigns');
    return response.data;
  }

  async createDonationCampaign(campaignData: any): Promise<ApiResponse<{ campaign: any }>> {
    const response = await this.client.post('/donation-campaigns', campaignData);
    return response.data;
  }

  async updateDonationCampaign(id: string, campaignData: any): Promise<ApiResponse<{ campaign: any }>> {
    const response = await this.client.put(`/donation-campaigns/${id}`, campaignData);
    return response.data;
  }

  async deleteDonationCampaign(id: string): Promise<ApiResponse> {
    const response = await this.client.delete(`/donation-campaigns/${id}`);
    return response.data;
  }

  // Stripe payment methods
  async createStripeCheckoutSession(data: {
    type: 'donation' | 'pooja_booking';
    amount: number;
    currency: string;
    campaignId?: string;
    poojaId?: string;
    templeId?: string;
    donorDetails?: {
      name: string;
      email: string;
      phone: string;
      address?: string;
    };
    successUrl: string;
    cancelUrl: string;
  }): Promise<ApiResponse<{ sessionId: string; sessionUrl: string }>> {
    const response = await this.client.post('/payments/create-checkout-session', data);
    return response.data;
  }

  async verifyStripePayment(sessionId: string): Promise<ApiResponse<{ payment: any }>> {
    const response = await this.client.post('/payments/verify-payment', { sessionId });
    return response.data;
  }


  async getPoojaStats(templeId?: string): Promise<ApiResponse<{ stats: any }>> {
    const response = await this.client.get('/poojas/stats/overview', {
      params: templeId ? { templeId } : {}
    });
    return response.data;
  }

  // User Management APIs
  async getUsers(params?: {
    page?: number;
    limit?: number;
    role?: string;
    search?: string;
  }): Promise<ApiResponse<{ users: User[] }>> {
    const response = await this.client.get('/users', { params });
    return response.data;
  }

  async createUser(userData: {
    name: string;
    email: string;
    role: 'super_admin' | 'admin' | 'temple_admin' | 'priest' | 'devotee';
    permissions?: string[];
    templeId?: string;
    phone?: string;
    password?: string;
  }): Promise<ApiResponse<{ user: User }>> {
    const response = await this.client.post('/users', userData);
    return response.data;
  }

  async getUserById(id: string): Promise<ApiResponse<{ user: User }>> {
    const response = await this.client.get(`/users/${id}`);
    return response.data;
  }

  async updateUser(id: string, userData: Partial<User>): Promise<ApiResponse<{ user: User }>> {
    // Determine which endpoint to use based on the data being updated
    if (userData.status !== undefined) {
      // Update user status
      const response = await this.client.put(`/users/${id}/status`, { isActive: userData.status === 'active' });
      return response.data;
    } else if (userData.role !== undefined) {
      // Update user role
      const response = await this.client.put(`/users/${id}/role`, userData);
      return response.data;
    } else {
      // For other updates, use role endpoint as fallback
      const response = await this.client.put(`/users/${id}/role`, userData);
      return response.data;
    }
  }

  async deleteUser(id: string): Promise<ApiResponse> {
    const response = await this.client.delete(`/users/${id}`);
    return response.data;
  }

  // Health check
  async healthCheck(): Promise<ApiResponse> {
    const response = await this.client.get('/health');
    return response.data;
  }

  // Profile methods
  async getProfile(): Promise<ApiResponse> {
    const response = await this.client.get('/profile');
    return response.data;
  }

  async updateProfile(data: any): Promise<ApiResponse> {
    const response = await this.client.put('/profile', data);
    return response.data;
  }

  async checkProfileComplete(): Promise<ApiResponse> {
    const response = await this.client.get('/profile/check-complete');
    return response.data;
  }

  // Website Configuration methods
  async getWebsiteConfig(): Promise<ApiResponse> {
    const response = await this.client.get('/website-config');
    return response.data;
  }

  async getPublicWebsiteConfig(): Promise<ApiResponse> {
    const response = await this.client.get('/website-config/public');
    return response.data;
  }

  async updateWebsiteConfig(config: any): Promise<ApiResponse> {
    const response = await this.client.put('/website-config', config);
    return response.data;
  }

  async resetWebsiteConfig(): Promise<ApiResponse> {
    const response = await this.client.post('/website-config/reset');
    return response.data;
  }

  async getThemeSettings(): Promise<ApiResponse> {
    const response = await this.client.get('/theme-settings');
    return response.data;
  }

  async updateThemeSettings(theme: {
    primaryColor?: string;
    secondaryColor?: string;
    tertiaryColor?: string;
    neutralColor?: string;
    fontFamily?: string;
  }): Promise<ApiResponse> {
    const response = await this.client.post('/theme-settings', theme);
    return response.data;
  }

  // Hero Banners (super_admin only)
  async getHeroBanners(params?: { page?: number; limit?: number; search?: string; isActive?: string }): Promise<ApiResponse<{ banners: HeroBanner[]; total: number; page: number; pages: number }>> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.search) searchParams.set('search', params.search);
    if (params?.isActive) searchParams.set('isActive', params.isActive);
    const response = await this.client.get(`/hero-banners?${searchParams.toString()}`);
    return response.data;
  }

  async createHeroBanner(data: Partial<HeroBanner>): Promise<ApiResponse<HeroBanner>> {
    const response = await this.client.post('/hero-banners', data);
    return response.data;
  }

  async updateHeroBanner(id: string, data: Partial<HeroBanner>): Promise<ApiResponse<HeroBanner>> {
    const response = await this.client.put(`/hero-banners/${id}`, data);
    return response.data;
  }

  async toggleHeroBanner(id: string): Promise<ApiResponse<HeroBanner>> {
    const response = await this.client.patch(`/hero-banners/${id}/toggle`);
    return response.data;
  }

  async deleteHeroBanner(id: string): Promise<ApiResponse> {
    const response = await this.client.delete(`/hero-banners/${id}`);
    return response.data;
  }

  // File Upload methods - type must match server ALLOWED_UPLOAD_TYPES
  // Valid types: temples, users, poojas, general, chat, hero-banners, donation-campaigns, causes, website-config, documents
  async uploadFile(type: string, file: File): Promise<ApiResponse> {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await this.client.post(`/upload/${type}/single`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      // Return error structure consistent with ApiResponse
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Upload failed',
        error: error.response?.data?.error || error.message
      };
    }
  }

  async uploadMultipleFiles(type: string, files: File[]): Promise<ApiResponse> {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    
    try {
      const response = await this.client.post(`/upload/${type}/multiple`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Upload failed',
        error: error.response?.data?.error || error.message
      };
    }
  }

  async deleteFile(type: string, filename: string): Promise<ApiResponse> {
    const response = await this.client.delete(`/upload/${type}/${filename}`);
    return response.data;
  }

  // Pooja Booking Management APIs
  async getPoojaBookings(params?: {
    page?: number;
    limit?: number;
    templeId?: string;
    status?: string;
    search?: string;
  }): Promise<ApiResponse<{ bookings: any[]; pagination: any }>> {
    const response = await this.client.get('/poojas/bookings/all', { params });
    return response.data;
  }

  async getPoojaBookingById(id: string): Promise<ApiResponse<{ booking: any }>> {
    const response = await this.client.get(`/poojas/bookings/${id}`);
    return response.data;
  }

  async updateBookingStatus(id: string, status: string, notes?: string): Promise<ApiResponse<{ booking: any }>> {
    const response = await this.client.put(`/poojas/bookings/${id}/status`, { status, notes });
    return response.data;
  }

  async cancelBooking(id: string, reason?: string): Promise<ApiResponse<{ booking: any }>> {
    const response = await this.client.post(`/poojas/bookings/${id}/cancel`, { reason });
    return response.data;
  }

  async getBookingsByTemple(templeId: string, params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<ApiResponse<{ bookings: any[]; pagination: any }>> {
    const response = await this.client.get(`/poojas/bookings/temple/${templeId}`, { params });
    return response.data;
  }

  // Temple Events APIs
  async getTempleEvents(params?: {
    page?: number;
    limit?: number;
    templeId?: string;
    search?: string;
  }): Promise<ApiResponse<{ events: any[] }> & { pagination?: any }> {
    const response = await this.client.get('/temple-events', { params });
    return response.data;
  }

  async createTempleEvent(data: any): Promise<ApiResponse<{ event: any }>> {
    const response = await this.client.post('/temple-events', data);
    return response.data;
  }

  async updateTempleEvent(id: string, data: any): Promise<ApiResponse<{ event: any }>> {
    const response = await this.client.put(`/temple-events/${id}`, data);
    return response.data;
  }

  async deleteTempleEvent(id: string): Promise<ApiResponse> {
    const response = await this.client.delete(`/temple-events/${id}`);
    return response.data;
  }

  // Sponsorships APIs
  async getSponsorships(params?: {
    page?: number;
    limit?: number;
    templeId?: string;
    search?: string;
  }): Promise<ApiResponse<{ sponsorships: any[] }> & { pagination?: any }> {
    const response = await this.client.get('/sponsorships', { params });
    return response.data;
  }

  async createSponsorship(data: any): Promise<ApiResponse<{ sponsorship: any }>> {
    if (data instanceof FormData) {
      const response = await this.client.post('/sponsorships', data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } else {
      const response = await this.client.post('/sponsorships', data);
      return response.data;
    }
  }

  async updateSponsorship(id: string, data: any): Promise<ApiResponse<{ sponsorship: any }>> {
    if (data instanceof FormData) {
      const response = await this.client.put(`/sponsorships/${id}`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } else {
      const response = await this.client.put(`/sponsorships/${id}`, data);
      return response.data;
    }
  }

  async deleteSponsorship(id: string): Promise<ApiResponse> {
    const response = await this.client.delete(`/sponsorships/${id}`);
    return response.data;
  }

  // Event Bookings - all (super_admin/admin) or by temple (temple_admin)
  async getAllEventBookings(params?: {
    page?: number;
    limit?: number;
    templeId?: string;
    status?: string;
  }): Promise<ApiResponse<{ bookings: any[] }> & { pagination?: any }> {
    const response = await this.client.get('/event-bookings/all', { params });
    return response.data;
  }

  async getEventBookingsByTemple(templeId: string, params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<ApiResponse<{ bookings: any[] }> & { pagination?: any }> {
    const response = await this.client.get(`/event-bookings/temple/${templeId}`, { params });
    return response.data;
  }

  async updateEventBookingStatus(id: string, status: string, notes?: string): Promise<ApiResponse<{ booking: any }>> {
    const response = await this.client.put(`/event-bookings/${id}/status`, { status, notes });
    return response.data;
  }

  // Sponsorship Bookings - all (super_admin/admin) or by temple (temple_admin)
  async getAllSponsorshipBookings(params?: {
    page?: number;
    limit?: number;
    templeId?: string;
    status?: string;
  }): Promise<ApiResponse<{ bookings: any[] }> & { pagination?: any }> {
    const response = await this.client.get('/sponsorship-bookings/all', { params });
    return response.data;
  }

  async getSponsorshipBookingsByTemple(templeId: string, params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<ApiResponse<{ bookings: any[] }> & { pagination?: any }> {
    const response = await this.client.get(`/sponsorship-bookings/temple/${templeId}`, { params });
    return response.data;
  }

  async updateSponsorshipBookingStatus(id: string, status: string, notes?: string): Promise<ApiResponse<{ booking: any }>> {
    const response = await this.client.put(`/sponsorship-bookings/${id}/status`, { status, notes });
    return response.data;
  }

  // Temple Wallet APIs
  async getWalletBalance(templeId: string): Promise<ApiResponse<{ wallet: any }>> {
    const response = await this.client.get(`/temple-wallet/${templeId}/balance`);
    return response.data;
  }

  async getWalletTransactions(templeId: string, params?: {
    page?: number;
    limit?: number;
    type?: string;
    source?: string;
  }): Promise<ApiResponse<{ transactions: any[]; pagination: any }>> {
    const response = await this.client.get(`/temple-wallet/${templeId}/transactions`, { params });
    return response.data;
  }

  async addManualTransaction(templeId: string, data: {
    type: 'credit' | 'debit';
    amount: number;
    description: string;
    metadata?: any;
  }): Promise<ApiResponse<{ transaction: any; wallet: any }>> {
    const response = await this.client.post(`/temple-wallet/${templeId}/manual-transaction`, data);
    return response.data;
  }

  async requestWithdrawal(templeId: string, data: {
    amount: number;
    bankDetails: {
      accountHolderName: string;
      accountNumber: string;
      bankName: string;
      ifscCode: string;
      branchName?: string;
    };
  }): Promise<ApiResponse<{ withdrawal: any }>> {
    const response = await this.client.post(`/temple-wallet/${templeId}/withdraw`, data);
    return response.data;
  }

  async getWithdrawalRequests(params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<ApiResponse<{ withdrawals: any[]; pagination: any }>> {
    const response = await this.client.get('/temple-wallet/withdrawals/all', { params });
    return response.data;
  }

  async processWithdrawal(withdrawalId: string, data: {
    status: 'approved' | 'rejected';
    transactionProof?: string;
    adminNotes?: string;
  }): Promise<ApiResponse<{ withdrawal: any }>> {
    const response = await this.client.put(`/temple-wallet/withdrawals/${withdrawalId}/process`, data);
    return response.data;
  }

  // Temple Registration APIs
  async getTempleRegistrations(params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<ApiResponse<{ registrations: any[]; pagination: any }>> {
    const response = await this.client.get('/temple-registration/all', { params });
    return response.data;
  }

  async getTempleRegistrationById(id: string): Promise<ApiResponse<{ registration: any }>> {
    const response = await this.client.get(`/temple-registration/${id}`);
    return response.data;
  }

  async approveTempleRegistration(id: string): Promise<ApiResponse<{ registration: any; temple: any; user: any }>> {
    const response = await this.client.put(`/temple-registration/${id}/approve`);
    return response.data;
  }

  async rejectTempleRegistration(id: string, data: {
    rejectionReason: string;
  }): Promise<ApiResponse<{ registration: any }>> {
    const response = await this.client.put(`/temple-registration/${id}/reject`, data);
    return response.data;
  }

  async assignRegistration(id: string, userId: string): Promise<ApiResponse<{ registration: any }>> {
    const response = await this.client.put(`/temple-registration/${id}/assign`, { adminId: userId });
    return response.data;
  }

  async resetTempleRegistrationToPending(id: string): Promise<ApiResponse<{ registration: any }>> {
    const response = await this.client.put(`/temple-registration/${id}/reset-pending`);
    return response.data;
  }

  async getTempleRegistrationHistory(id: string): Promise<ApiResponse<{ history: any[] }>> {
    const response = await this.client.get(`/temple-registration/${id}/history`);
    return response.data;
  }

  // Page Management APIs
  
  async getPages(params?: {
    published?: boolean;
  }): Promise<ApiResponse<{ pages: Page[] }>> {
    const response = await this.client.get('/pages', { params });
    return response.data;
  }

  async getPageById(id: string): Promise<ApiResponse<{ page: Page }>> {
    const response = await this.client.get(`/pages/${id}`);
    return response.data;
  }

  async createPage(pageData: any): Promise<ApiResponse<{ page: Page }>> {
    const response = await this.client.post('/pages', pageData);
    return response.data;
  }

  async updatePage(id: string, pageData: any): Promise<ApiResponse<{ page: Page }>> {
    const response = await this.client.put(`/pages/${id}`, pageData);
    return response.data;
  }

  async deletePage(id: string): Promise<ApiResponse> {
    const response = await this.client.delete(`/pages/${id}`);
    return response.data;
  }





  async updateVerificationStatus(id: string, data: {
    type: string;
    status: string;
    notes?: string;
    screenshot?: string;
  }): Promise<ApiResponse<{ registration: any }>> {
    const response = await this.client.put(`/temple-registration/${id}/verify`, data);
    return response.data;
  }

  async getMyTemple(): Promise<ApiResponse<{ temple: Temple }>> {
    const response = await this.client.get('/temples/my-temple');
    return response.data;
  }

  async getUserRegistrationStatus(email: string): Promise<ApiResponse<{ registration: any }>> {
    const response = await this.client.get(`/temple-registration/status/user/${email}`);
    return response.data;
  }

  async deleteTempleRegistration(id: string): Promise<ApiResponse> {
    const response = await this.client.delete(`/temple-registration/${id}`);
    return response.data;
  }


  // Donation Cause APIs
  async getDonationCauses(): Promise<ApiResponse<DonationCause[]>> {
    const response = await this.client.get('/donation-causes');
    return response.data;
  }

  async createDonationCause(data: Partial<DonationCause>): Promise<ApiResponse<DonationCause>> {
    const response = await this.client.post('/donation-causes', data);
    return response.data;
  }

  async updateDonationCause(id: string, data: Partial<DonationCause>): Promise<ApiResponse<DonationCause>> {
    const response = await this.client.put(`/donation-causes/${id}`, data);
    return response.data;
  }

  async deleteDonationCause(id: string): Promise<ApiResponse> {
    const response = await this.client.delete(`/donation-causes/${id}`);
    return response.data;
  }


  // Subscription Plan APIs
  async getSubscriptionPlans(params?: {
    isActive?: boolean;
  }): Promise<ApiResponse<{ plans: SubscriptionPlan[] }>> {
    const response = await this.client.get('/subscription-plans', { params });
    return response.data;
  }

  async getSubscriptionPlanById(id: string): Promise<ApiResponse<{ plan: SubscriptionPlan }>> {
    const response = await this.client.get(`/subscription-plans/${id}`);
    return response.data;
  }

  async createSubscriptionPlan(data: Partial<SubscriptionPlan>): Promise<ApiResponse<{ plan: SubscriptionPlan }>> {
    const response = await this.client.post('/subscription-plans', data);
    return response.data;
  }

  async updateSubscriptionPlan(id: string, data: Partial<SubscriptionPlan>): Promise<ApiResponse<{ plan: SubscriptionPlan }>> {
    const response = await this.client.put(`/subscription-plans/${id}`, data);
    return response.data;
  }

  async deleteSubscriptionPlan(id: string): Promise<ApiResponse> {
    const response = await this.client.delete(`/subscription-plans/${id}`);
    return response.data;
  }

  // Admin: Assign subscription plan to temple
  async assignSubscriptionToTemple(templeId: string, planId: string, registrationId?: string): Promise<ApiResponse<{ temple: any }>> {
    const response = await this.client.post(`/temples/${templeId}/subscription`, { planId, registrationId });
    return response.data;
  }

  // Admin: Get temple subscription info
  async getTempleSubscriptionInfo(templeId: string): Promise<ApiResponse<{ subscription: any; plan: any }>> {
    const response = await this.client.get(`/temples/${templeId}/subscription`);
    return response.data;
  }

  // Admin: Remove subscription from temple
  async removeSubscriptionFromTemple(templeId: string): Promise<ApiResponse<{ temple: any }>> {
    const response = await this.client.delete(`/temples/${templeId}/subscription`);
    return response.data;
  }

  async uploadImage(file: File): Promise<ApiResponse<{ filename: string; path?: string; url?: string; fullPath: string }>> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.client.post('/upload/causes/single', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Sankalpam Lists APIs
  async getSankalpamLists(templeId: string): Promise<ApiResponse<any>> {
    const response = await this.client.get(`/sankalpam/lists/${templeId}`);
    return response.data;
  }

  async getSankalpamDevotees(listId: string): Promise<ApiResponse<any>> {
    const response = await this.client.get(`/sankalpam/devotees`, {
      params: { id: listId }
    });
    return response.data;
  }

  async toggleSankalpamStar(data: { templeId: string, sourceType: string, sourceId: string }): Promise<ApiResponse<any>> {
    const response = await this.client.post('/sankalpam/toggle-star', data);
    return response.data;
  }

  // Support Tickets APIs
  async getTickets(params?: {
    status?: string;
    priority?: string;
    search?: string;
    temple?: string;
  }): Promise<ApiResponse<{ tickets: Ticket[] }>> {
    const response = await this.client.get('/tickets', { params });
    return response.data;
  }

  async getTicketById(id: string): Promise<ApiResponse<{ ticket: Ticket }>> {
    const response = await this.client.get(`/tickets/${id}`);
    return response.data;
  }

  async updateTicketStatus(id: string, status: string): Promise<ApiResponse<{ ticket: Ticket }>> {
    const response = await this.client.patch(`/tickets/${id}/status`, { status });
    return response.data;
  }

  async addTicketReply(id: string, formData: FormData): Promise<ApiResponse<{ ticket: Ticket }>> {
    const response = await this.client.post(`/tickets/${id}/reply`, formData);
    return response.data;
  }
}

// Donation Cause interface
export interface DonationCause {
  _id: string;
  title: string;
  description: string;
  suggestedAmount?: number;
  isCustomAmount: boolean;
  minAmount?: number;
  image?: string;
  icon?: string;
  isActive: boolean;
  isPopular: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
}

export interface HeroBanner {
  _id: string;
  image: string;
  link: string;
  title: string;
  subtitle?: string;
  order: number;
  isActive: boolean;
  badge?: string;
  description?: string;
  primaryCTALabel?: string;
  primaryCTALink?: string;
  secondaryCTALabel?: string;
  secondaryCTALink?: string;
  createdAt: string;
  updatedAt: string;
}

// Subscription Plan interface
export interface SubscriptionPlan {
  _id: string;
  name: string;
  slug: string;
  description: string;
  price: number;
  currency: string;
  duration: number;
  billingCycle: 'monthly' | 'yearly';
  features: {
    maxDonations?: number | null;
    maxPoojas?: number | null;
    maxPriests?: number | null;
    analytics?: boolean;
    customDomain?: boolean;
    apiAccess?: boolean;
    prioritySupport?: boolean;
    customFeatures?: string[];
  };
  stripePriceId?: string;
  stripeProductId?: string;
  isActive: boolean;
  isPopular?: boolean;
  displayOrder: number;
  subscriptionStatus?: 'active' | 'inactive' | 'cancelled' | 'expired';
  subscriptionPlan?: string | SubscriptionPlan;
  subscriptionEndDate?: string;
  createdAt: string;
  updatedAt: string;
}

// Create and export singleton instance
export const adminApiClient = new AdminApiClient();
export default adminApiClient;
